import React, { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Building2, Users, HardDrive, Settings as SettingsIcon, MapPin, Plane, UserCircle, ShieldCheck, Mail, Fingerprint, ClipboardList, MessageSquare, MessageCircle, Navigation, Hash, ShieldAlert, Sparkles } from 'lucide-react';

const ALL_LINKS = [
  { id: 'companies',       label: 'Company Management',    description: 'Manage companies and branches',                  icon: Building2,    path: '/settings/companies',        adminOnly: false },
  { id: 'users',           label: 'User Management',        description: 'Manage employees and roles',                     icon: Users,        path: '/settings/users',            adminOnly: false },
  { id: 'role-permissions',label: 'Role Permissions',       description: 'Configure module access per role',               icon: ShieldAlert,  path: '/settings/role-permissions', adminOnly: true  },
  { id: 'locations',       label: 'Location Master',         description: 'Airports, ports, warehouses, ICDs',             icon: MapPin,       path: '/settings/locations',        adminOnly: false },
  { id: 'carriers',        label: 'Carrier Master',          description: 'Airlines, shipping lines, couriers',            icon: Plane,        path: '/settings/carriers',         adminOnly: false },
  { id: 'agents',          label: 'Agent Master',            description: 'Logistics agents and representatives',          icon: UserCircle,   path: '/settings/agents',           adminOnly: false },
  { id: 'custodians',      label: 'Custodian Master',        description: 'Custodians used on Lifting Advices',            icon: ShieldCheck,  path: '/settings/custodians',       adminOnly: false },
  { id: 'drive',           label: 'Drive Settings',          description: 'Connect Google Drive storage',                  icon: HardDrive,    path: '/settings/drive',            adminOnly: false },
  { id: 'email',           label: 'Email & SMTP',            description: 'Gmail SMTP, templates & notifications',         icon: Mail,         path: '/settings/email',            adminOnly: false },
  { id: 'attestr',         label: 'Attestr API',             description: 'GST lookup — fetch company & branches',         icon: Fingerprint,  path: '/settings/attestr',          adminOnly: false },
  { id: 'docket-series',   label: 'Global Docket Series',    description: 'Single docket counter for all companies',       icon: Hash,         path: '/settings/docket-series?tab=docket', adminOnly: true  },
  { id: 'booking-series',  label: 'Booking Number Series',   description: 'Prefix & sequential counter for booking requests', icon: ClipboardList, path: '/settings/docket-series?tab=booking', adminOnly: true  },
  { id: 'sms',             label: 'SMS Settings',            description: 'SMS gateway for docket notifications',           icon: MessageSquare,path: '/settings/sms',              adminOnly: true  },
  { id: 'whatsapp',        label: 'WhatsApp Settings',       description: 'WhatsApp messages via smswaapi.com',             icon: MessageCircle,path: '/settings/whatsapp',         adminOnly: true  },
  { id: 'fms',             label: 'FMS / Tracking',          description: 'Locanix FMS token & API URL for live tracking',  icon: Navigation,   path: '/settings/fms',              adminOnly: true  },
  { id: 'activity-log',    label: 'Activity Log',            description: 'Audit trail — who created what',                 icon: ClipboardList,path: '/settings/activity-log',     adminOnly: true  },
  { id: 'ai',              label: 'AI Engine & Copilot',     description: 'Gemini / OpenAI keys for error audit & chat copilot', icon: Sparkles,     path: '/settings/ai',               adminOnly: true  },
];

const Settings = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      setIsAdmin(['super_admin', 'company_admin'].includes(payload.role));
    } catch {}
  }, []);

  const settingsLinks = ALL_LINKS.filter(l => !l.adminOnly || isAdmin);
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-8 h-8 text-text-dark" />
        <div>
          <h1 data-testid="settings-heading" className="text-3xl font-bold text-text-dark font-manrope">Settings</h1>
          <p data-testid="settings-subtitle" className="text-text-secondary font-ibm mt-1">
            Manage system configurations and master data
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 h-fit">
          <div className="space-y-2" data-testid="settings-submenu">
            {settingsLinks.map((link) => {
              const Icon = link.icon;
              return (
                <NavLink
                  key={link.id}
                  to={link.path}
                  data-testid={`settings-nav-${link.id}`}
                  className={({ isActive }) =>
                    `flex items-start gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-accent-blue/10 text-accent-blue border border-accent-blue/20'
                        : 'text-text-secondary hover:bg-gray-50'
                    }`
                  }
                >
                  <div className="mt-1">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-ibm text-sm font-semibold">{link.label}</p>
                    <p className="text-xs text-text-secondary font-ibm mt-1">{link.description}</p>
                  </div>
                </NavLink>
              );
            })}
          </div>
        </aside>

        <section className="min-w-0" data-testid="settings-content">
          <Outlet />
        </section>
      </div>
    </div>
  );
};

export default Settings;
